import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import {
  getEpisodeWorksheet,
  updateEpisodeWorksheet,
} from '../../services/api';
import type {
  CurrentConditionIndicator,
  DiagnosisSuccessor,
  EpisodeWorksheetClaimPeriod,
  EpisodeWorksheetRead,
  EpisodeWorksheetUpdate,
  UsState,
} from '../../services/api/types.gen';
import type { PrimaryDiagnosisFitness } from '../../services/api/types.gen';
import { USSTATE_VALUES } from '../../services/api/models/UsState';
import {
  X12_UNSAFE_REMARKS_MESSAGE,
  X12_UNSAFE_REMARKS_PATTERN,
} from '../billing/worksheet/types';
import { US_STATES } from '../../constants/options';
import PrimaryFitnessConfirmDialog from '../PrimaryFitnessConfirmDialog';
import { DiagnosisCodeValidityWarning } from '../DiagnosisCodeValidityWarning';
import { usePrimaryDiagnosisFitness } from '../../hooks/usePrimaryDiagnosisFitness';
import {
  fitnessPresentation,
  shouldDiscourage,
} from '../../utils/primaryDiagnosisFitness';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { isValidDollarAmount } from '../../utils/currencyUtils';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { DateField, DropdownField, Field } from '../FormFields';
import { ConditionCodesField } from '../billing/worksheet/ConditionCodesField';
import { EditSection } from '../billing/worksheet/EditSection';
import { OccurrenceCodesField } from '../billing/worksheet/OccurrenceCodesField';
import { OccurrenceSpansField } from '../billing/worksheet/OccurrenceSpansField';
import { ValueCodesField } from '../billing/worksheet/ValueCodesField';
import InlineDiagnosisSearch from '../oasis/InlineDiagnosisSearch';

const STATE_LABEL: Record<string, string> = Object.fromEntries(
  US_STATES.map(s => [s.value, s.label])
);
// Leading empty option lets the user clear the field back to null under full-replace PUT.
const stateOptions = [
  { value: '', label: '—' },
  ...USSTATE_VALUES.map(v => ({
    value: v,
    label: `${v} — ${STATE_LABEL[v] ?? v}`,
  })),
];
export function EpisodeWorksheetEditor({
  episodeId,
  onClose,
}: {
  episodeId: string;
  onClose: () => void;
}) {
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // One piece of edit state mirroring EpisodeWorksheetUpdate.
  const [employer, setEmployer] = useState('');
  const [employment, setEmployment] = useState(false);
  const [autoAccident, setAutoAccident] = useState(false);
  const [otherAccident, setOtherAccident] = useState(false);
  const [accidentState, setAccidentState] = useState<UsState | ''>('');
  const [accidentDate, setAccidentDate] = useState<string | null>(null);
  // Values not currently shown in the UI, but preserved on full-replace PUT so
  // saving visible worksheet edits does not silently erase existing data.
  const [illnessDate, setIllnessDate] = useState<string | null>(null);
  const [conditionIndicator, setConditionIndicator] = useState<
    CurrentConditionIndicator | ''
  >('');
  const [hospFrom, setHospFrom] = useState<string | null>(null);
  const [hospTo, setHospTo] = useState<string | null>(null);
  const [outsideLab, setOutsideLab] = useState('');
  // Codes (Task 9).
  const [diagnosisCodes, setDiagnosisCodes] = useState<string[]>([]);
  // One worksheet feeds every claim of the episode. A code is valid or not
  // on a claim's Through date, but Medicare groups the claim with the grouper
  // version its From date selects, mapping a code that version lacks to its
  // predecessor on a claim that spans into the next (CMS Claims Processing
  // Manual Ch.10 §80.2). So validity and replacements follow the Through
  // date and principal fitness follows the From date. The codes are judged
  // for one claim at a time: the latest by default, switchable when the
  // episode has more.
  const [claimPeriods, setClaimPeriods] = useState<
    EpisodeWorksheetClaimPeriod[]
  >([]);
  const [selectedPeriod, setSelectedPeriod] = useState<
    EpisodeWorksheetClaimPeriod | undefined
  >(undefined);
  const claimThroughDate = selectedPeriod?.end_date;
  const claimFromDate = selectedPeriod?.start_date;
  const [pendingPrincipal, setPendingPrincipal] = useState<{
    code: string;
    fitness: PrimaryDiagnosisFitness | null;
    // The retired principal a successor chip would replace; absent when the
    // picker is seating a first code.
    replaces?: string;
  } | null>(null);
  // Fitness judges only the principal. Validity (deleted or header codes)
  // covers every code, so ask about all of them by the same route the rest of
  // the app uses, so the answer cannot diverge per surface.
  const principalCode = diagnosisCodes[0];
  const {
    fitnessByCode,
    invalidByCode,
    successorsByCode,
    spanPredecessorByCode,
    pdgmRelevant,
    lookupFailed,
  } = usePrimaryDiagnosisFitness(diagnosisCodes, {
    episodeId,
    asOf: claimThroughDate,
    claimFromDate,
  });
  // Secondaries get their own validity line under the chips.
  const invalidSecondaryCodes = diagnosisCodes
    .slice(1)
    .filter(code => invalidByCode[code]);
  const principalWarning = principalCode
    ? fitnessPresentation(fitnessByCode[principalCode], {
        softened: !pdgmRelevant,
      })
    : null;
  // Fitness (From date's grouper) and validity (Through date) can disagree on
  // a span claim: I42.0 still groups but is a header on the Through date. The
  // fitness warning speaks for an invalid principal when there is one;
  // otherwise the principal needs its own validity line.
  const principalInvalid =
    !!principalCode && !!invalidByCode[principalCode] && !principalWarning;
  // Swap a retired code for the replacement the user clicked, in the same
  // seat. Never automatic: the conversion table lists candidates, and the
  // right one depends on the documentation. The replacement takes the retired
  // code's seat even when it is already on the worksheet further down, so a
  // retired principal (FL 67) is replaced, never dropped: its other
  // occurrence goes instead.
  const replaceCode = (retired: string, replacement: string) =>
    setDiagnosisCodes(codes =>
      codes
        .map(c => (c === retired ? replacement : c))
        .filter((c, i, all) => all.indexOf(c) === i)
    );
  // A replacement for the principal lands in FL 67, so it gets the same
  // confirm as a picker pick when its own fitness is discouraged. Secondaries
  // are not judged on fitness and replace directly.
  const replaceWithSuccessor = (
    retired: string,
    successor: DiagnosisSuccessor
  ) => {
    if (
      retired === principalCode &&
      pdgmRelevant &&
      shouldDiscourage(successor.primary_fitness)
    ) {
      setPendingPrincipal({
        code: successor.code,
        fitness: successor.primary_fitness ?? null,
        replaces: retired,
      });
      return;
    }
    replaceCode(retired, successor.code);
  };
  const successorChips = (code: string) => (
    <SuccessorChips
      code={code}
      successors={invalidByCode[code] ? successorsByCode[code] : undefined}
      onReplace={replaceWithSuccessor}
    />
  );
  const [conditionCodes, setConditionCodes] = useState<string[]>([]);
  const [occurrenceCodes, setOccurrenceCodes] = useState<
    { code: string; date: string }[]
  >([]);
  const [valueCodes, setValueCodes] = useState<
    { code: string; amount: string }[]
  >([]);
  const [occurrenceSpans, setOccurrenceSpans] = useState<
    { code: string; from_date: string; through_date: string }[]
  >([]);
  const [remarks, setRemarks] = useState('');

  const seed = (d: EpisodeWorksheetRead) => {
    setEmployer(d.employer ?? '');
    setEmployment(d.condition_related_to_employment);
    setAutoAccident(d.condition_related_to_auto_accident);
    setOtherAccident(d.condition_related_to_other_accident);
    setAccidentState((d.auto_accident_state as UsState) ?? '');
    setAccidentDate(d.accident_date ?? null);
    setIllnessDate(d.date_of_current_illness_or_injury ?? null);
    setConditionIndicator(
      (d.current_condition_indicator as CurrentConditionIndicator) ?? ''
    );
    setHospFrom(d.hospitalization_from_date ?? null);
    setHospTo(d.hospitalization_to_date ?? null);
    setOutsideLab(
      d.outside_lab_charge != null ? String(d.outside_lab_charge) : ''
    );
    setDiagnosisCodes(d.diagnosis_codes ?? []);
    setConditionCodes(d.condition_codes ?? []);
    setOccurrenceCodes(
      (d.occurrence_codes ?? []).map(r => ({ code: r.code, date: r.date }))
    );
    setValueCodes(
      (d.value_codes ?? []).map(r => ({ code: r.code, amount: r.amount }))
    );
    setOccurrenceSpans(
      (d.occurrence_spans ?? []).map(r => ({
        code: r.code,
        from_date: r.from_date,
        through_date: r.through_date,
      }))
    );
    setRemarks(d.remarks ?? '');
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      try {
        const resp = await getEpisodeWorksheet({
          path: { episode_id: episodeId },
        });
        if (!active) return;
        if (resp.error || !resp.data) {
          setLoadError(true);
          return;
        }
        seed(resp.data);
        const periods = resp.data.claim_periods;
        setClaimPeriods(periods);
        setSelectedPeriod(periods[periods.length - 1]);
      } catch (e) {
        if (active) setLoadError(true);
        console.error('Failed to load episode worksheet', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [episodeId]);

  const anyFlag = employment || autoAccident || otherAccident;

  // Mirror ClaimWorksheetEditor: BLOCK on partial rows (one field filled, the other blank)
  // and on a span whose through_date precedes its from_date. Fully-blank rows are dropped (not blocked).
  const validate = (): string | null => {
    if (anyFlag && !employer.trim() && !accidentState)
      return "When a claim is marked as workers' comp or accident-related, you must provide an employer or an accident state.";
    const partialOcc = occurrenceCodes.some(
      r => Boolean(r.code) !== Boolean(r.date)
    );
    const partialVal = valueCodes.some(
      r => Boolean(r.code) !== Boolean(r.amount)
    );
    const partialSpan = occurrenceSpans.some(
      r =>
        [r.code, r.from_date, r.through_date].some(Boolean) &&
        ![r.code, r.from_date, r.through_date].every(Boolean)
    );
    if (partialOcc || partialVal || partialSpan)
      return 'Complete or remove code rows missing a date or amount.';
    const badSpanOrder = occurrenceSpans.some(
      r => r.from_date && r.through_date && r.through_date < r.from_date
    );
    if (badSpanOrder)
      return 'Occurrence span end date must be on or after the start date.';

    if (
      valueCodes.some(
        v => v.amount.trim() !== '' && !isValidDollarAmount(v.amount.trim())
      )
    )
      return 'Enter a valid dollar amount for each value code.';
    // The backend refuses these before they reach the 837I as free text.
    if (X12_UNSAFE_REMARKS_PATTERN.test(remarks))
      return X12_UNSAFE_REMARKS_MESSAGE;
    return null;
  };

  // After validate() passes, only fully-complete rows remain meaningful; drop fully-blank rows.
  const buildBody = (): EpisodeWorksheetUpdate => ({
    employer: employer.trim() || null,
    accident_date: accidentDate || null,
    condition_related_to_employment: employment,
    condition_related_to_auto_accident: autoAccident,
    condition_related_to_other_accident: otherAccident,
    auto_accident_state: accidentState || null,
    date_of_current_illness_or_injury: illnessDate || null,
    current_condition_indicator: conditionIndicator || null,
    hospitalization_from_date: hospFrom || null,
    hospitalization_to_date: hospTo || null,
    outside_lab_charge: outsideLab.trim() === '' ? null : outsideLab.trim(),
    diagnosis_codes: diagnosisCodes,
    condition_codes: conditionCodes.map(c => c.trim()).filter(Boolean),
    occurrence_codes: occurrenceCodes.filter(r => r.code && r.date),
    occurrence_spans: occurrenceSpans.filter(
      r => r.code && r.from_date && r.through_date
    ),
    value_codes: valueCodes.filter(r => r.code && r.amount),
    remarks: remarks.trim() || null,
  });

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setValidationError(v);
      return;
    }
    setValidationError(null);
    setSaving(true);
    try {
      const resp = await updateEpisodeWorksheet({
        path: { episode_id: episodeId },
        body: buildBody(),
      });
      if (resp.error || !resp.data) throw resp;
      seed(resp.data);
      showSuccess('Worksheet saved', 'Episode worksheet was updated.');
      onClose();
    } catch (e) {
      console.error('Failed to save episode worksheet', e);
      showError(
        'Save failed',
        getUserFacingApiMessage(
          e,
          'Unable to save the worksheet. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={o => {
        if (!o) onClose();
      }}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-5xl h-[85vh] max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0'>
        <DialogHeader className='border-b bg-gradient-to-r from-slate-50 to-white px-6 py-4'>
          <DialogTitle>Episode Worksheet</DialogTitle>
          <DialogDescription className='sr-only'>
            Editable UB-04 claim worksheet fields for the selected episode.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className='min-h-0 flex-1 py-8 text-center text-sm text-muted-foreground'>
            Loading…
          </p>
        ) : loadError ? (
          <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
            <div className='rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
              Unable to load the worksheet. Please try again.
            </div>
          </div>
        ) : (
          <main
            data-testid='episode-worksheet-scroll-region'
            className='min-h-0 flex-1 overflow-y-auto bg-slate-50/60 px-6 py-5'
          >
            <fieldset disabled={saving} className='min-w-0 space-y-5'>
              <EditSection title='Condition / Accident'>
                <div className='grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-[minmax(15rem,1fr)_minmax(10rem,12rem)_minmax(10rem,12rem)_minmax(14rem,1.25fr)]'>
                  {/* Single accessible name per checkbox: id+htmlFor label (no aria-label → avoids double-labeling). */}
                  <div className='space-y-2 rounded-lg border border-slate-200 bg-white/70 p-3'>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='ew-employment'
                        checkboxSize='sm'
                        checked={employment}
                        onCheckedChange={c => setEmployment(Boolean(c))}
                      />
                      <label htmlFor='ew-employment' className='text-sm'>
                        Related to employment (FL 10a)
                      </label>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='ew-auto-accident'
                        checkboxSize='sm'
                        checked={autoAccident}
                        onCheckedChange={c => setAutoAccident(Boolean(c))}
                      />
                      <label htmlFor='ew-auto-accident' className='text-sm'>
                        Related to auto accident (FL 10b)
                      </label>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id='ew-other-accident'
                        checkboxSize='sm'
                        checked={otherAccident}
                        onCheckedChange={c => setOtherAccident(Boolean(c))}
                      />
                      <label htmlFor='ew-other-accident' className='text-sm'>
                        Related to other accident (FL 10c)
                      </label>
                    </div>
                  </div>
                  <div className='max-w-48'>
                    <DropdownField
                      label='Accident state (FL 29)'
                      htmlFor='ew-accident-state'
                      value={accidentState}
                      options={stateOptions}
                      onChange={v => setAccidentState(v as UsState | '')}
                      fieldSize='sm'
                    />
                  </div>
                  <div className='max-w-48'>
                    <DateField
                      label='Accident date (FL 31)'
                      htmlFor='ew-accident-date'
                      value={accidentDate}
                      onChange={setAccidentDate}
                      fieldSize='sm'
                      typable
                    />
                  </div>
                  <Field
                    label='Employer'
                    htmlFor='ew-employer'
                    value={employer}
                    onChange={setEmployer}
                    fieldSize='sm'
                  />
                </div>
              </EditSection>
              <EditSection title='Diagnoses'>
                <ClaimPeriodLine
                  periods={claimPeriods}
                  selected={selectedPeriod}
                  onChange={setSelectedPeriod}
                />
                {lookupFailed && (
                  <p
                    className='mb-2 text-xs leading-snug text-destructive'
                    data-testid='worksheet-codes-unchecked'
                  >
                    Codes could not be checked for this claim.
                  </p>
                )}
                {principalWarning && (
                  <p
                    className={`mb-2 text-xs leading-snug ${principalWarning.toneClass}`}
                    data-testid='worksheet-principal-fitness-warning'
                  >
                    {principalCode} is the principal diagnosis (FL 67).{' '}
                    {principalWarning.message}
                    {spanPredecessorByCode[principalCode] &&
                      ` Medicare groups it as ${spanPredecessorByCode[principalCode]} on this claim.`}
                  </p>
                )}
                {principalInvalid && (
                  <div className='mb-2'>
                    <DiagnosisCodeValidityWarning
                      code={principalCode}
                      reason={invalidByCode[principalCode]}
                    />
                  </div>
                )}
                {principalCode && successorChips(principalCode)}
                <div className='flex flex-wrap gap-2'>
                  {diagnosisCodes.map((code, index) => (
                    <span
                      key={code}
                      data-testid='worksheet-diagnosis-chip'
                      className={`inline-flex items-center gap-1 whitespace-nowrap rounded bg-muted px-2 py-1 font-mono text-sm ${
                        (index > 0 && invalidByCode[code]) ||
                        (index === 0 && principalInvalid)
                          ? 'border border-destructive text-destructive'
                          : ''
                      }`}
                    >
                      {code}
                      <button
                        type='button'
                        aria-label={`Remove ${code}`}
                        onClick={() =>
                          setDiagnosisCodes(
                            diagnosisCodes.filter(c => c !== code)
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {invalidSecondaryCodes.length > 0 && (
                  <div className='mt-2 space-y-2'>
                    {invalidSecondaryCodes.map(code => (
                      <div key={code} className='space-y-1'>
                        <DiagnosisCodeValidityWarning
                          code={code}
                          reason={invalidByCode[code]}
                        />
                        {successorChips(code)}
                      </div>
                    ))}
                  </div>
                )}
                <InlineDiagnosisSearch
                  placeholder='Search diagnosis code…'
                  asOf={claimThroughDate}
                  claimFromDate={claimFromDate}
                  excludeCodes={diagnosisCodes}
                  // The first code on the worksheet is the claim's principal
                  // diagnosis (FL 67), so only an empty list seats one here.
                  primaryContext={diagnosisCodes.length === 0}
                  pdgmRelevant={pdgmRelevant}
                  onSelect={(code, _desc, fitness) => {
                    if (diagnosisCodes.includes(code)) return;
                    const seatsPrincipal = diagnosisCodes.length === 0;
                    if (
                      seatsPrincipal &&
                      pdgmRelevant &&
                      shouldDiscourage(fitness)
                    ) {
                      setPendingPrincipal({ code, fitness: fitness ?? null });
                      return;
                    }
                    setDiagnosisCodes([...diagnosisCodes, code]);
                  }}
                />
              </EditSection>
              <EditSection title='Condition Codes (FL 18–28)'>
                <ConditionCodesField
                  values={conditionCodes}
                  onChange={setConditionCodes}
                />
              </EditSection>
              <EditSection title='Occurrence Codes (FL 31–34)'>
                <OccurrenceCodesField
                  rows={occurrenceCodes}
                  onChange={setOccurrenceCodes}
                />
              </EditSection>
              <EditSection title='Value Codes (FL 39–41)'>
                <ValueCodesField rows={valueCodes} onChange={setValueCodes} />
              </EditSection>
              <EditSection title='Occurrence Spans (FL 35–36)'>
                <OccurrenceSpansField
                  rows={occurrenceSpans}
                  onChange={setOccurrenceSpans}
                />
              </EditSection>
              <EditSection title='Remarks (FL 80)'>
                <Textarea
                  textareaSize='sm'
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  aria-invalid={X12_UNSAFE_REMARKS_PATTERN.test(remarks)}
                />
              </EditSection>
            </fieldset>
          </main>
        )}
        <div className='flex items-center justify-end gap-2 border-t px-6 py-4'>
          {validationError && (
            <span className='mr-auto text-sm text-red-600'>
              {validationError}
            </span>
          )}
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={saving || loading || loadError}
          >
            {saving ? 'Saving…' : 'Save & Close'}
          </Button>
        </div>
      </DialogContent>
      <PrimaryFitnessConfirmDialog
        code={pendingPrincipal?.code ?? null}
        fitness={pendingPrincipal?.fitness ?? null}
        onConfirm={() => {
          if (pendingPrincipal?.replaces) {
            replaceCode(pendingPrincipal.replaces, pendingPrincipal.code);
          } else if (pendingPrincipal) {
            setDiagnosisCodes([...diagnosisCodes, pendingPrincipal.code]);
          }
          setPendingPrincipal(null);
        }}
        onCancel={() => setPendingPrincipal(null)}
      />
    </Dialog>
  );
}

/**
 * Which claim the worksheet's codes are judged for. A switcher only when the
 * episode has more than one claim period; the codes and the picker follow it.
 */
function ClaimPeriodLine({
  periods,
  selected,
  onChange,
}: {
  periods: EpisodeWorksheetClaimPeriod[];
  selected: EpisodeWorksheetClaimPeriod | undefined;
  onChange: (period: EpisodeWorksheetClaimPeriod | undefined) => void;
}) {
  if (!selected) return null;
  const label = 'Codes valid for the claim ending';
  if (periods.length < 2) {
    return (
      <p
        className='mb-3 text-xs text-muted-foreground'
        data-testid='worksheet-claim-period'
      >
        {label} {formatDateToMMDDYYYY(selected.end_date)}
      </p>
    );
  }
  // Two payers' cycles can end on the same day from different From dates,
  // and the From date picks the grouper, so a period is both dates. Its
  // Through date alone names it unless another period shares that date.
  const periodKey = (period: EpisodeWorksheetClaimPeriod) =>
    `${period.start_date}|${period.end_date}`;
  const periodName = (period: EpisodeWorksheetClaimPeriod) =>
    periods.filter(p => p.end_date === period.end_date).length > 1
      ? `${formatDateToMMDDYYYY(period.start_date)} – ${formatDateToMMDDYYYY(period.end_date)}`
      : formatDateToMMDDYYYY(period.end_date);
  return (
    <div
      className='mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'
      data-testid='worksheet-claim-period'
    >
      <span
        id='ew-claim-period-label'
        className='text-xs text-muted-foreground'
      >
        {label}
      </span>
      <Select
        value={periodKey(selected)}
        onValueChange={(key: string) =>
          onChange(periods.find(p => periodKey(p) === key))
        }
      >
        <SelectTrigger
          selectSize='sm'
          className='w-full sm:w-auto sm:min-w-40'
          aria-labelledby='ew-claim-period-label'
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {periods.map(period => (
            <SelectItem key={periodKey(period)} value={periodKey(period)}>
              {periodName(period)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * The CMS conversion table's replacements for a code not valid on the claim's
 * Through date. Each one replaces the code in place, only when clicked. No
 * chips when the table lists no direct conversion (S23.420A): the warning
 * stands alone.
 */
function SuccessorChips({
  code,
  successors,
  onReplace,
}: {
  code: string;
  successors: DiagnosisSuccessor[] | undefined;
  onReplace: (retired: string, successor: DiagnosisSuccessor) => void;
}) {
  if (!successors || successors.length === 0) return null;
  return (
    <div
      className='flex flex-wrap gap-1'
      data-testid='worksheet-successor-chips'
      data-code={code}
    >
      {successors.map(successor => (
        // The code never breaks; at 390px a long description truncates inside
        // the chip (full text in the tooltip and the accessible name) rather
        // than pushing the dialog wider.
        <Button
          key={successor.code}
          type='button'
          variant='outline'
          size='sm'
          className='min-w-0 max-w-full'
          title={`Replace with ${successor.code} — ${successor.desc}`}
          onClick={() => onReplace(code, successor)}
        >
          <span className='whitespace-nowrap'>
            Replace with{' '}
            <span className='font-mono font-semibold'>{successor.code}</span>
          </span>{' '}
          <span className='min-w-0 truncate'>— {successor.desc}</span>
        </Button>
      ))}
    </div>
  );
}
